/**
 * Session Details Form Component
 *
 * Handles the basic session information section of the CreateAuditSessionDialog.
 * Extracted to reduce component complexity and improve maintainability.
 */

import { Card, Heading, Section, Text, TextInput, TextArea } from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';

import * as styles from '../CreateAuditSessionDialog.css';
import { getMinStartDate, getMaxEndDate, getMaxEndDateForStart, getMinStartDateForEnd } from '../utils/ValidationHelpers';

interface SessionDetailsFormProps {
  sessionName: string;
  sessionDescription: string;
  communityName: string;
  startDate: string;
  endDate: string;
  dateError: string;
  totalLoading: boolean;
  onSessionNameChange: (value: string) => void;
  onSessionDescriptionChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
}

export function SessionDetailsForm({
  sessionName,
  sessionDescription,
  communityName,
  startDate,
  endDate,
  dateError,
  totalLoading,
  onSessionNameChange,
  onSessionDescriptionChange,
  onStartDateChange,
  onEndDateChange,
}: SessionDetailsFormProps) {
  const handleSessionNameChange = useCallback((value: string) => onSessionNameChange(value), [onSessionNameChange]);
  const handleSessionDescriptionChange = useCallback((value: string) => onSessionDescriptionChange(value), [onSessionDescriptionChange]);

  const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onStartDateChange(e.target.value), [onStartDateChange]);

  const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onEndDateChange(e.target.value), [onEndDateChange]);

  const handleDescriptionTextAreaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => handleSessionDescriptionChange(e.target.value),
    [handleSessionDescriptionChange]
  );

  // Calculate date picker constraints
  // Start date: absolute min is 90 days ago, but also constrained by end date (max 29-day span)
  const minStartDate = useMemo(() => getMinStartDateForEnd(endDate), [endDate]);
  const maxEndDate = useMemo(() => getMaxEndDate(), []);

  // End date: max is today, but also constrained by start date (max 29-day span)
  const maxEndDateForStart = useMemo(() => getMaxEndDateForStart(startDate), [startDate]);
  const minEndDate = useMemo(() => startDate || getMinStartDate(), [startDate]);

  return (
    <Section spacing="medium">
      <Card>
        <Section spacing="medium">
          <Heading level="h3">Session Details</Heading>
          <Text>Basic information about your audit session</Text>

          {/* Session Name and Community - Row 1 */}
          <div className={`${styles.formGrid} ${styles.formRow}`}>
            <div className={styles.formField}>
              <Text>Session Name *</Text>
              <TextInput
                disabled={totalLoading}
                id="session-name"
                placeholder="Enter a descriptive name for your audit session"
                value={sessionName}
                onChange={handleSessionNameChange}
              />
              {!sessionName.trim() && <Text className={styles.errorText}>Session name is required</Text>}
            </div>

            <div className={styles.formField}>
              <Text>Community</Text>
              <TextInput disabled id="community-name" placeholder="Community name" value={communityName} />
              <Text style={{ fontSize: '14px', color: '#6b7280' }}>Sessions are created for the current community</Text>
            </div>
          </div>

          {/* Description - Row 2 */}
          <div className={`${styles.formField} ${styles.formRow}`}>
            <TextArea
              hideResize
              disabled={totalLoading}
              id="session-description"
              placeholder="Optional description of the audit session purpose"
              rows={3}
              title="Description"
              value={sessionDescription}
              onChange={handleDescriptionTextAreaChange}
            />
          </div>

          {/* Date Fields - Row 3 */}
          <div className={`${styles.formGrid} ${styles.formRow}`}>
            <div className={styles.formField}>
              <Text>Start Date (UTC) *</Text>
              <input
                className={styles.dateInput}
                disabled={totalLoading}
                id="start-date"
                max={maxEndDate}
                min={minStartDate}
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
              />
            </div>

            <div className={styles.formField}>
              <Text>End Date (UTC) *</Text>
              <input
                className={styles.dateInput}
                disabled={totalLoading}
                id="end-date"
                max={maxEndDateForStart}
                min={minEndDate}
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
              />
            </div>
          </div>

          {/* Date validation error */}
          {dateError && (
            <div className={styles.formRow}>
              <Text className={styles.errorText}>{dateError}</Text>
            </div>
          )}
        </Section>
      </Card>
    </Section>
  );
}
